import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import pluckrLogo from '../assets/images/pluckr-logo.png';
import ThemeToggle from '@/components/ThemeToggle';
import { useThemeMode } from '@/hooks/useThemeMode';

export default function Profile() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const brandGreen = '#9be38f';
  const { resolvedTheme } = useThemeMode();
  const isDark = resolvedTheme === 'dark';

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      // Not logged in, redirect to login
      navigate('/early-access');
      return;
    }

    try {
      setUser(JSON.parse(userData));
    } catch (error) {
      console.error('Failed to parse user data:', error);
      navigate('/early-access');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    setTimeout(() => {
      navigate('/');
    }, 1000);
  };

  if (!user) {
    return null; // Loading or redirecting
  }

  return (
    <div className="min-h-screen w-full bg-background grid-pattern" data-testid="profile-page">
      <Toaster
        position="top-right"
        theme={resolvedTheme}
        toastOptions={{
          style: {
            background: isDark ? '#282929' : '#ffffff',
            color: isDark ? '#ffffff' : '#1a1a1a',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e5e5e5',
          },
        }}
      />

      <ThemeToggle className="fixed top-4 right-4 z-[60]" />

      {/* Top-left brand */}
      <div className="px-8 pt-7">
        <Link
          to="/"
          className="inline-flex items-center gap-3 text-black/90 dark:text-white"
          style={{ fontFamily: "'Lora', Georgia, serif" }}
        >
          <img src={pluckrLogo} alt="Pluckr" className="h-9 w-auto" />
          <span className="text-[22px] font-bold tracking-[-0.01em]">Pluckr</span>
        </Link>
      </div>

      <div className="max-w-[1200px] mx-auto px-8 pb-16 pt-10">
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-14">
          {/* Left headline */}
          <div className="flex-1 w-full lg:pt-10">
            <h1
              className="text-[56px] sm:text-[72px] font-black leading-[0.95] tracking-[-0.03em]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="block text-black/80 dark:text-white">Your</span>
              <span className="block" style={{ color: brandGreen }}>
                Profile
              </span>
            </h1>
          </div>

          {/* Right card */}
          <div className="w-full max-w-[420px]">
            <div
              className="bg-white dark:bg-[#282929] rounded-[18px] shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-black/5 dark:border-white/15 p-8"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {/* Profile Avatar */}
              <div className="flex justify-center mb-6">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold"
                  style={{ backgroundColor: brandGreen, color: '#1a1a1a' }}
                >
                  {user.email?.charAt(0).toUpperCase() || 'U'}
                </div>
              </div>

              {/* User Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[12px] font-medium text-black/70 dark:text-white/70">Email Address</label>
                  <div className="w-full h-11 rounded-md border border-black/10 dark:border-white/20 px-3 text-[14px] flex items-center bg-black/[0.02] dark:bg-white/5 text-black/80 dark:text-white/85">
                    {user.email}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[12px] font-medium text-black/70 dark:text-white/70">Account Status</label>
                  <div className="w-full h-11 rounded-md border border-black/10 dark:border-white/20 px-3 text-[14px] flex items-center gap-2 bg-black/[0.02] dark:bg-white/5">
                    {user.isVerified ? (
                      <>
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        <span className="text-black/80 dark:text-white/85">Verified</span>
                      </>
                    ) : (
                      <>
                        <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                        <span className="text-black/80 dark:text-white/85">Pending Verification</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[12px] font-medium text-black/70 dark:text-white/70">Member Since</label>
                  <div className="w-full h-11 rounded-md border border-black/10 dark:border-white/20 px-3 text-[14px] flex items-center bg-black/[0.02] dark:bg-white/5 text-black/80 dark:text-white/85">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'N/A'}
                  </div>
                </div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full h-11 rounded-full font-semibold text-[13px] text-white transition-colors mt-6"
                  style={{ backgroundColor: '#1f2d2a' }}
                >
                  Log Out
                </button>

                <Link
                  to="/"
                  className="block text-center text-[13px] text-black/50 dark:text-white/60 hover:text-black/70 dark:hover:text-white pt-2"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
